import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import ErrorBoundary from './ErrorBoundary';
import { useAuth } from '../context/AuthContext';

import CuisineCardsSelector from './CuisineCardsSelector';
import DietCardsSelector from './DietCardsSelector';
import FoodTypeCardsSelector from './FoodTypeCardsSelector';
import TagCardsSelector from './TagCardSelector';
import MyRecipes from './MyRecipes';
import MyFavorites from './MyFavorites';
import TrendingRecipes from './TrendingRecipes';
import RecipesForYou from './RecipesForYou';
import RecipesForPantry from './RecipesForPantry';
import RecipesForPantryIngredientPills from './RecipesForPantryIngredientPills';
import RecipesForPantryRankedByPantryMatch from './RecipesForPantryRankedByPantryMatch';

import RecipeList from '../pages/RecipeList';
import RecipesForIngredients from '../pages/RecipesForIngredients';

import RecipesForDiet from '../pages/RecipesForDiet';
import RecipesForCuisine from '../pages/RecipesForCuisine';
import RecipesForFoodType from '../pages/RecipesForFoodType';
import RecipesForTag from '../pages/RecipesForTag';


const DynamicExploreRecipes = ({ autoFocusSearch = false, onMealAdded }) => {
    const { sessionToken } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState('');
    const searchInputRef = useRef(null);
    
    // Add local state for immediate visual feedback
    const [activeCategory, setActiveCategory] = useState(null);

    // Read state from URL
    const selectedCategory = searchParams.get('category');
    const filterType = searchParams.get('filter');
    const filterId = searchParams.get('filterId');
    const filterName = searchParams.get('filterName');
    const filterDescription = searchParams.get('filterDescription');
    
    // Sync activeCategory with selectedCategory when URL changes
    useEffect(() => {
        setActiveCategory(selectedCategory);
    }, [selectedCategory]);
    
    // Reconstruct selectedFilter from URL params
    const selectedFilter = filterType ? {
        type: filterType,
        data: {
            id: filterId,
            name: filterName,
            description: filterDescription
        }
    } : null;
    const categories = [
        // { id: 'pantry', label: "Based on Your Pantry" },
        { id: 'pantryRankedByPantryMatch', label: "Based on Your Pantry" },
        { id: 'pantryPills', label: "By Ingredient" },
        // { id: 'forYou', label: 'For You' },
        { id: 'all', label: 'All Recipes' },
        // { id: 'myRecipes', label: 'My Recipes' },
        { id: 'favorites', label: 'Favorites' },
        { id: 'trending', label: 'Trending' },
        // { id: 'recentlyViewed', label: 'Recently Viewed' },
        // { id: 'ingredients', label: 'Ingredients' },
        
    ];

    const getCategoryButtonStyle = (categoryId) => {
        // Use activeCategory for immediate visual feedback
        const isSelected = activeCategory === categoryId;
        return {
            backgroundColor: isSelected ? '#515151' : 'white',
            color: isSelected ? 'white' : '#6E7180',
            border: isSelected ? '1px solid #515151' : `1px solid #9DA2B3`,
            padding: '0.5rem 1rem',
            borderRadius: '0.25rem',
            cursor: 'pointer',
            // fontSize: '0.8rem',
            fontWeight: '500'
        };
    };

    const handleCategoryClick = (categoryId) => {
        if (selectedCategory === categoryId) {
            // If clicking the same category, go back to default view
            setActiveCategory(null);
            setSearchParams({});
        } else {
            // Set active category immediately for instant visual feedback
            setActiveCategory(categoryId);
            // Set the category in URL
            setSearchParams({ category: categoryId });
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // Auto-focus search input when component mounts (if autoFocusSearch is true)
    useEffect(() => {
        if (autoFocusSearch && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [autoFocusSearch]);

    // Auto-select "All Recipes" when user starts typing with no category selected
    useEffect(() => {
        if (searchTerm && !selectedCategory && !selectedFilter) {
            setSearchParams({ category: 'all' });
        }
    }, [searchTerm, selectedCategory, selectedFilter, setSearchParams]);

    // Handle card clicks
    const handleDietClick = (diet) => {
        const params = { 
            filter: 'diet', 
            filterId: diet.id, 
            filterName: diet.name
        };
        if (diet.description) params.filterDescription = diet.description;
        setSearchParams(params);
    };

    const handleCuisineClick = (cuisine) => {
        const params = { 
            filter: 'cuisine', 
            filterId: cuisine.id, 
            filterName: cuisine.name
        };
        if (cuisine.description) params.filterDescription = cuisine.description;
        setSearchParams(params);
    };

    const handleFoodTypeClick = (foodType) => {
        const params = { 
            filter: 'foodType', 
            filterId: foodType.id, 
            filterName: foodType.name
        };
        if (foodType.description) params.filterDescription = foodType.description;
        setSearchParams(params);
    };

    const handleTagClick = (tag) => {
        const params = { 
            filter: 'tag', 
            filterId: tag.id, 
            filterName: tag.name
        };
        if (tag.description) params.filterDescription = tag.description;
        setSearchParams(params);
    };

    const handleBackToSelectors = () => {
        // Use browser's back button functionality
        window.history.back();
    };

    const renderCategoryContent = () => {
        switch (selectedCategory) {
            case 'all':
                return <RecipeList searchTerm={searchTerm} onMealAdded={onMealAdded} />;
            case 'forYou':
                return <RecipesForYou searchTerm={searchTerm} onMealAdded={onMealAdded} />;
            case 'myRecipes':
                return <MyRecipes sessionToken={sessionToken} searchTerm={searchTerm} onMealAdded={onMealAdded} />;
            case 'favorites':
                return <MyFavorites sessionToken={sessionToken} searchTerm={searchTerm} onMealAdded={onMealAdded} />;
            case 'trending':
                return <TrendingRecipes sessionToken={sessionToken} searchTerm={searchTerm} onMealAdded={onMealAdded} />;
            case 'recentlyViewed':
                return (
                    <div className="text-center my-5">
                        <p className="text-muted">Recently Viewed recipes coming soon!</p>
                    </div>
                );
            case 'ingredients':
                return <RecipesForIngredients searchTerm={searchTerm} onMealAdded={onMealAdded} />;
            case 'pantry':
                return <RecipesForPantry searchTerm={searchTerm} onMealAdded={onMealAdded} />;
            case 'pantryPills':
                return <RecipesForPantryIngredientPills searchTerm={searchTerm} onMealAdded={onMealAdded} />;
            case 'pantryRankedByPantryMatch':
                return <RecipesForPantryRankedByPantryMatch searchTerm={searchTerm} onMealAdded={onMealAdded} />;
            default:
                return null;
        }
    };

    return (
        <div className="dynamic-explore-recipes">
            {/* Search Bar */}
            <div className="search-bar-container mb-3">
                <div className="position-relative" style={{ maxWidth: '860px', margin: '0 auto', padding: '0 1rem' }}>
                    <svg 
                        width="20" 
                        height="20" 
                        viewBox="0 0 16 16" 
                        fill="currentColor"
                        style={{ 
                            position: 'absolute', 
                            left: 'calc(1rem + 15px)', 
                            top: '50%', 
                            transform: 'translateY(-50%)',
                            opacity: 0.5,
                            color: '#6c757d'
                        }}
                    >
                        <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
                    </svg>
                    <input
                        ref={searchInputRef}
                        type="text"
                        className="form-control-custom"
                        placeholder="Search recipes or ingredients..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        style={{
                            padding: '0.75rem 1rem 0.75rem 45px',
                            borderRadius: '4px',
                            border: '1px solid #dee2e6',
                            fontSize: '1rem'
                        }}
                    />
                </div>
            </div>

            {/* Category Buttons */}
            <div className="category-buttons mb-4">
                <div className="d-flex flex-wrap gap-2 align-items-center" style={{ maxWidth: '860px', margin: '0 auto', padding: '0 1rem', justifyContent: 'flex-start' }}>
                    {/* <span style={{ fontWeight: '600', marginRight: '0.5rem' }}>Search By:</span> */}
                    {categories.map((category) => (
                        <button
                            key={category.id}
                            onClick={() => handleCategoryClick(category.id)}
                            style={getCategoryButtonStyle(category.id)}
                            className="category-btn"
                        >
                            {category.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content Area */}
            {selectedCategory || selectedFilter ? (
                <div className="category-content">
                    {/* <div className="mb-3" style={{ maxWidth: '860px', margin: '0 auto', padding: '0 1rem', textAlign: 'left' }}>
                        <Button 
                            variant="link" 
                            onClick={handleBackToSelectors}
                            style={{ color: '#52cd92', textDecoration: 'none', padding: 0, textAlign: 'left' }}
                        >
                            ← Back
                        </Button>
                    </div> */}
                    {selectedCategory && renderCategoryContent()}
                    {selectedFilter && (
                        <>
                            {selectedFilter.type === 'diet' && (
                                <RecipesForDiet diet={selectedFilter.data} searchTerm={searchTerm} onMealAdded={onMealAdded} />
                            )}
                            {selectedFilter.type === 'cuisine' && (
                                <RecipesForCuisine cuisine={selectedFilter.data} searchTerm={searchTerm} onMealAdded={onMealAdded} />
                            )}
                            {selectedFilter.type === 'foodType' && (
                                <RecipesForFoodType foodType={selectedFilter.data} searchTerm={searchTerm} onMealAdded={onMealAdded} />
                            )}
                            {selectedFilter.type === 'tag' && (
                                <RecipesForTag tag={selectedFilter.data} searchTerm={searchTerm} onMealAdded={onMealAdded} />
                            )}
                        </>
                    )}
                </div>
            ) : (
                <div className="default-sections">
                    {/* Based on Your Pantry - Empty for now */}
                    {/* <div className="section mb-5">
                        <h2 className="mb-3">For You</h2>
                        <div className="text-center py-4 text-muted">
                            <p>Recipes based on what you've already</p>
                        </div>
                    </div> */}

                    {/* By Cuisine */}
                    <div className="section mb-5">
                        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0' }}>
                            <h2 className="mb-3" style={{ textAlign: 'left' }}>Cuisines</h2>
                        </div>
                        <ErrorBoundary 
                            fallbackMessage={
                                <div className="alert alert-warning">
                                    <p>Unable to load cuisine options. Please try refreshing the page.</p>
                                    <Button size="sm" onClick={() => window.location.reload()}>
                                        Refresh Page
                                    </Button>
                                </div>
                            }
                        >
                            <CuisineCardsSelector sessionToken={sessionToken} onCuisineClick={handleCuisineClick} />
                        </ErrorBoundary>
                    </div>

                    {/* By Diet */}
                    <div className="section mb-5">
                        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0' }}>
                            <h2 className="mb-3" style={{ textAlign: 'left' }}>Diets</h2>
                        </div>
                        <ErrorBoundary 
                            fallbackMessage={
                                <div className="alert alert-warning">
                                    <p>Unable to load diet options. Please try refreshing the page.</p>
                                    <Button size="sm" onClick={() => window.location.reload()}>
                                        Refresh Page
                                    </Button>
                                            </div>
                                        }
                                    >
                                        <DietCardsSelector sessionToken={sessionToken} onDietClick={handleDietClick} />
                                    </ErrorBoundary>
                                </div>

                                {/* By Food Type */}
                                <div className="section mb-5">
                                    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0' }}>
                                        <h2 className="mb-3" style={{ textAlign: 'left' }}>Food Types</h2>
                                    </div>
                                    <ErrorBoundary 
                                        fallbackMessage={
                                            <div className="alert alert-warning">
                                                <p>Unable to load food type options. Please try refreshing the page.</p>
                                                <Button size="sm" onClick={() => window.location.reload()}>
                                                    Refresh Page
                                                </Button>
                                            </div>
                                        }
                                    >
                                        <FoodTypeCardsSelector sessionToken={sessionToken} onFoodTypeClick={handleFoodTypeClick} />
                                    </ErrorBoundary>
                                </div>

                                {/* By Tag */}
                                <div className="section mb-5">
                                    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0' }}>
                                        <h2 className="mb-3" style={{ textAlign: 'left' }}>More</h2>
                                    </div>
                                    <ErrorBoundary 
                                        fallbackMessage={
                                            <div className="alert alert-warning">
                                                <p>Unable to load tag options. Please try refreshing the page.</p>
                                                <Button size="sm" onClick={() => window.location.reload()}>
                                                    Refresh Page
                                                </Button>
                                            </div>
                                        }
                                    >
                                        <TagCardsSelector sessionToken={sessionToken} onTagClick={handleTagClick} />
                                    </ErrorBoundary>
                                </div>
                </div>
            )}
        </div>
    );
};

export default DynamicExploreRecipes;

